import { useLayoutEffect, useRef, useState } from "react";
import { FiSend } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import useChatFiles from "../hooks/useChatFiles";
import { filesFrom } from "../utils/chatFiles";
import ChatAttachMenu from "./ChatAttachMenu";
import ChatAttachment from "./ChatAttachment";
import ChatPendingFiles from "./ChatPendingFiles";

/**
 * A support conversation's messages and the box for the next one. The same on both sides:
 * the person at /support, and admins in the panel's Support tab. `side` is who's looking —
 * "user" or "support" — and their own messages sit on the right.
 *
 * The parent loads and sends; this only shows. `onSend(text)` resolves true once sent.
 * Photos, videos and files go through `onSendFile(file, caption, onProgress)`, one message
 * each, the typed text as the first one's caption; `fileUrl(message, { download })` shows them.
 */
const SupportThread = ({
  side,
  messages,
  isDark,
  /** When the other side last read the conversation, for "Seen" under the latest own message. */
  seenAt = null,
  hasMore = false,
  loadingEarlier = false,
  onLoadEarlier,
  onSend,
  onSendFile = null,
  fileUrl = null,
  /** Why a file couldn't be picked, e.g. too big. */
  onFileError = null,
  sending = false,
  disabled = false,
  placeholder,
  emptyText,
  /** A line above the box, e.g. that the conversation was closed. */
  notice = null,
  /** A name over each run of messages by the same author, or null for none. */
  senderLabel = () => null,
}) => {
  const { t, language } = useTranslation();
  const [draft, setDraft] = useState("");
  const chatFiles = useChatFiles({ onError: onFileError });
  const [sendingFiles, setSendingFiles] = useState(false);
  const [draggingFiles, setDraggingFiles] = useState(false);
  const canAttach = !!onSendFile && !disabled;
  const hasPending = chatFiles.pending.length > 0;
  const listRef = useRef(null);
  const inputRef = useRef(null);
  /** The list as last drawn, to tell new messages at the bottom from earlier ones on top. */
  const drawn = useRef({ first: null, last: null, height: 0, top: 0 });

  const isOwn = (m) => (side === "support" ? !!m.fromSupport : !m.fromSupport);
  const locale = language === "ka" ? "ka-GE" : "en-GB";
  const when = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const today = new Date().toDateString() === d.toDateString();
    return today
      ? d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: false })
      : d.toLocaleString(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
  };

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const first = messages[0]?.id || null;
    const last = messages[messages.length - 1]?.id || null;
    const before = drawn.current;
    if (before.first && first !== before.first && last === before.last) {
      // Earlier messages came in on top: keep the reader where they were.
      el.scrollTop = el.scrollHeight - before.height + before.top;
    } else if (last !== before.last) {
      const wasAtBottom = before.height - before.top - el.clientHeight < 160;
      const mine = messages.length && isOwn(messages[messages.length - 1]);
      if (!before.last || wasAtBottom || mine) el.scrollTop = el.scrollHeight;
    }
    drawn.current = { first, last, height: el.scrollHeight, top: el.scrollTop };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const remember = () => {
    const el = listRef.current;
    if (el) drawn.current = { ...drawn.current, height: el.scrollHeight, top: el.scrollTop };
  };

  const sendFiles = async () => {
    const caption = draft.trim();
    setSendingFiles(true);
    try {
      const done = await chatFiles.sendAll(async (file, index, onProgress) => {
        const ok = await onSendFile(file, index === 0 ? caption : "", onProgress);
        if (ok && index === 0) setDraft("");
        return ok;
      });
      if (done) setTimeout(() => inputRef.current?.focus(), 0);
    } finally {
      setSendingFiles(false);
    }
  };

  const busy = sending || sendingFiles;

  const send = async (e) => {
    e?.preventDefault();
    if (busy || disabled) return;
    if (hasPending) return sendFiles();
    const text = draft.trim();
    if (!text) return;
    const ok = await onSend(text);
    if (ok) {
      setDraft("");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const lastOwn = [...messages].reverse().find(isOwn);
  const seen = !!(lastOwn && seenAt && new Date(seenAt) >= new Date(lastOwn.createdAt));

  const muted = "text-label-secondary";
  const theirs = "border border-separator-default bg-bg-raised text-label-primary";
  const inputClass = "border-transparent bg-fill-tertiary text-label-primary placeholder:text-label-tertiary focus:ring-2 focus:ring-focus-ring";

  const dropHandlers = canAttach
    ? {
        onDragOver: (e) => {
          if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
          e.preventDefault();
          if (!draggingFiles) setDraggingFiles(true);
        },
        onDragLeave: (e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setDraggingFiles(false);
        },
        onDrop: (e) => {
          const dropped = filesFrom(e.dataTransfer);
          setDraggingFiles(false);
          if (!dropped.length) return;
          e.preventDefault();
          chatFiles.addFiles(dropped);
          inputRef.current?.focus();
        },
      }
    : {};

  return (
    <div className="relative flex min-h-0 flex-1 flex-col" {...dropHandlers}>
      {draggingFiles && (
        <div className="pointer-events-none absolute inset-2 z-40 grid place-items-center rounded-radius-md border-2 border-dashed border-accent-default bg-ink/70 text-subheadline-semibold text-accent-text backdrop-blur-sm">
          {t("chatFiles.dropHere")}
        </div>
      )}
      <div ref={listRef} onScroll={remember} className="min-h-0 flex-1 space-y-1 overflow-y-auto px-4 py-4 sm:px-6 chat-scrollbar">
        {hasMore && (
          <div className="flex justify-center pb-3">
            <button
              type="button"
              onClick={onLoadEarlier}
              disabled={loadingEarlier}
              className={`rounded-full px-3 py-1 text-footnote-semibold transition disabled:opacity-50 ${
                "bg-fill-tertiary text-label-secondary hover:bg-fill-quaternary"
              }`}
            >
              {loadingEarlier ? t("common.loading") : t("support.loadEarlier")}
            </button>
          </div>
        )}
        {messages.length === 0 ? (
          <div className={`flex h-full items-center justify-center px-6 text-center text-subheadline ${muted}`}>{emptyText}</div>
        ) : (
          messages.map((m, i) => {
            const own = isOwn(m);
            const label = senderLabel(m);
            const prev = messages[i - 1];
            const showLabel = label && (!prev || isOwn(prev) !== own || senderLabel(prev) !== label);
            return (
              <div key={m.id} className={`flex flex-col ${own ? "items-end" : "items-start"} ${showLabel && i > 0 ? "pt-3" : ""}`}>
                {showLabel && <span className={`mb-1 px-1 text-caption-1-semibold ${muted}`}>{label}</span>}
                <div
                  className={`max-w-[85%] rounded-radius-md px-3.5 py-2 text-subheadline leading-relaxed shadow-sm sm:max-w-[75%] ${
                    own ? "bg-accent-default text-accent-on" : theirs
                  }`}
                >
                  {m.attachment?.kind && fileUrl && (
                    <div className={m.content ? "mb-1.5" : ""}>
                      <ChatAttachment
                        attachment={m.attachment}
                        url={fileUrl(m)}
                        downloadUrl={fileUrl(m, { download: true })}
                        own={own}
                        isDark={isDark}
                      />
                    </div>
                  )}
                  {m.content && <p className="whitespace-pre-wrap break-words">{m.content}</p>}
                </div>
                <span className={`mt-0.5 px-1 text-caption-1 ${muted}`}>
                  {when(m.createdAt)}
                  {seen && m.id === lastOwn.id ? ` · ${t("support.seen")}` : ""}
                </span>
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={send} className={`border-t px-3 py-3 sm:px-4 border-separator-default`}>
        {notice && <p className={`mb-2 px-1 text-footnote ${muted}`}>{notice}</p>}
        <ChatPendingFiles items={chatFiles.pending} onRemove={chatFiles.remove} isDark={isDark} busy={sendingFiles} />
        <div className="flex items-end gap-2">
          {onSendFile && (
            <ChatAttachMenu
              onPick={(picked) => {
                chatFiles.addFiles(picked);
                inputRef.current?.focus();
              }}
              isDark={isDark}
              disabled={busy || disabled}
            />
          )}
          <textarea
            ref={inputRef}
            value={draft}
            rows={1}
            maxLength={5000}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) send(e);
            }}
            onPaste={(e) => {
              if (!canAttach) return;
              const pasted = filesFrom(e.clipboardData);
              if (!pasted.length) return;
              e.preventDefault();
              chatFiles.addFiles(pasted);
            }}
            placeholder={hasPending ? t("chatFiles.addCaption") : placeholder}
            disabled={disabled}
            className={`max-h-40 min-h-[44px] flex-1 resize-none rounded-radius-md border px-4 py-2.5 text-subheadline outline-none transition [field-sizing:content] disabled:opacity-50 ${inputClass}`}
          />
          <button
            type="submit"
            disabled={(!draft.trim() && !hasPending) || busy || disabled}
            aria-label={t("support.send")}
            title={t("support.send")}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-radius-md bg-accent-default text-accent-on shadow-sm transition hover:bg-mintStrong disabled:cursor-not-allowed disabled:opacity-40"
          >
            <FiSend className={`h-4 w-4 ${busy ? "animate-pulse" : ""}`} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default SupportThread;
